import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { Link, NavLink, Route, Routes, useNavigate, useParams } from 'react-router-dom'
import RequestError from '../components/RequestError'
import { engineName } from '../lib/navigation'
import { NotFound } from '../components/SectionNav'
import DatabaseBadge from '../components/DatabaseBadge'
import DomainsTab from '../components/DomainsTab'
import DeployTimeline from '../components/DeployTimeline'
import type { DeployActivity } from '../components/LogConsole'
import {
  IconDots,
  IconExternal,
  IconEye,
  IconEyeOff,
  IconInbox,
  IconPlus,
  IconTrash,
} from '../components/icons'
import { Drawer, Menu, Tooltip } from '../components/overlay'
import StatusBadge, { isInFlight } from '../components/StatusBadge'
import {
  Button,
  ConfirmDialog,
  CopyButton,
  EmptyState,
  Field,
  ListSkeleton,
  Skeleton,
} from '../components/ui'
import { api } from '../lib/api'
import { useLiveRefresh } from '../lib/events'
import { deployTitle, duration, relativeTime, shortSha } from '../lib/format'
import { useToast } from '../lib/toast'
import type { Deployment, ServiceDetail as Detail } from '../lib/types'
import { useApi } from '../lib/useApi'
import {
  changeSet,
  mergeFromServer,
  rebaseAfterSave,
  rowsFromServer,
  type EnvRow,
} from '../lib/envRows'

// xterm is most of the bundle, so the console only loads when a log is
// actually opened — which is exactly when the drawer mounts.
const LogConsole = lazy(() => import('../components/LogConsole'))

export default function ServiceDetail() {
  const { serviceId = '' } = useParams()
  const navigate = useNavigate()
  const { attempt } = useToast()
  const [action, setAction] = useState<string | null>(null)

  const { data: service, error, reload } = useApi(
    () => api.service(serviceId),
    [serviceId],
    60_000,
  )
  // Anything about this service — its own status, or a deploy of it — refreshes
  // the header, the runtime badge and the deploy list together.
  useLiveRefresh(
    reload,
    (event) => event.id === serviceId || event.service_id === serviceId,
  )

  if (error) return <div className="alert error">{error}</div>
  if (!service) return <ServiceSkeleton />

  const run = async (
    label: string,
    fn: () => Promise<unknown>,
    success: string,
  ) => {
    setAction(label)
    await attempt(fn, { success })
    setAction(null)
    reload()
  }

  const base = `/services/${service.id}`
  const suspended = service.status === 'suspended'

  return (
    <>
      <header className="page-head">
        <div className="titles">
          <div className="title-row">
            <h1>{service.name}</h1>
            <StatusBadge status={service.status} />
          </div>
          <p className="subtitle tiny">
            <span className="mono">{service.repo_full_name}</span>
            <span className="sep">·</span>
            <span className="mono">{service.branch}</span>
            {service.url && (
              <>
                <span className="sep">·</span>
                <a
                  className="row mono"
                  style={{ gap: 5, color: 'var(--brand-text)' }}
                  href={service.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {service.url.replace('https://', '')}
                  <IconExternal className="" size={12} />
                </a>
              </>
            )}
          </p>
        </div>

        {/* One primary action, everything else behind a menu: a row of five
            equal buttons makes the common case no faster to find than the
            rare one. */}
        <div className="row">
          <Button
            variant="primary"
            loading={action === 'deploy'}
            disabled={action !== null}
            onClick={() =>
              run(
                'deploy',
                async () => {
                  const release = await api.deployApplication(service.id)
                  navigate(`/projects/${release.project_id}`)
                },
                'Application deployment started',
              )
            }
          >
            Deploy application
          </Button>

          <Menu
            label={`More actions for ${service.name}`}
            actions={[
              {
                label: 'Deploy only this service',
                disabled: action !== null,
                onSelect: () => run('deploy', async () => {
                  const deployment = await api.deploy(service.id)
                  navigate(`/deploys/${deployment.id}`)
                }, 'Single-service deployment started'),
              },
              {
                label: 'Restart',
                disabled: service.status !== 'live',
                hint: service.status !== 'live' ? 'not running' : undefined,
                onSelect: () =>
                  run('restart', () => api.restart(service.id), `${service.name} is restarting`),
              },
              suspended
                ? {
                    label: 'Resume',
                    onSelect: () =>
                      run('resume', () => api.resume(service.id), `${service.name} is starting`),
                  }
                : {
                    label: 'Suspend',
                    onSelect: () =>
                      run(
                        'suspend',
                        () => api.suspend(service.id),
                        `${service.name} is suspended`,
                      ),
                  },
              {
                label: 'Open in a new tab',
                disabled: !service.url,
                onSelect: () => window.open(service.url ?? '', '_blank', 'noreferrer'),
              },
              {
                label: 'Settings',
                onSelect: () => navigate(`${base}/settings`),
              },
            ]}
            trigger={
              <button type="button" className="btn icon" disabled={action !== null}>
                <IconDots className="" size={16} />
              </button>
            }
          />
        </div>
      </header>

      {service.runtime?.reason && (
        <div className="alert warn stack" style={{ gap: 8, alignItems: 'stretch' }}>
          <div>
            {service.runtime.ready_replicas === 0 ? 'Not running' : 'Running'} —{' '}
            {service.runtime.reason}
          </div>

          {/*
            The reason above is about Kubernetes: "back-off 5m0s restarting
            failed container" says nothing a developer can act on. What the
            app printed before it died is the thing they came here for, and it
            lives in a container that has already been replaced — so it is
            carried out and shown, with the platform's reading of it first
            where there is one.
          */}
          {service.runtime.explanation && (
            <div>{service.runtime.explanation}</div>
          )}
          {service.runtime.last_output.length > 0 && (
            <pre
              className="tiny mono"
              style={{ margin: 0, overflowX: 'auto', whiteSpace: 'pre-wrap' }}
            >
              {service.runtime.last_output.join('\n')}
            </pre>
          )}
        </div>
      )}

      <nav className="tabs" aria-label="Service sections">
        <NavLink end to={base}>
          Deploys
        </NavLink>
        <NavLink to={`${base}/logs`}>Logs</NavLink>
        <NavLink to={`${base}/metrics`}>Metrics</NavLink>
        <NavLink to={`${base}/env`}>Environment</NavLink>
        <NavLink to={`${base}/databases`}>Databases</NavLink>
        <NavLink to={`${base}/domains`}>Domains</NavLink>
        <NavLink to={`${base}/settings`}>Settings</NavLink>
      </nav>

      <Routes>
        <Route index element={<DeploysTab service={service} />} />
        <Route path="logs" element={<LogsTab serviceId={service.id} />} />
        <Route path="metrics" element={<MetricsTab serviceId={service.id} />} />
        <Route path="env" element={<EnvTab key={service.id} serviceId={service.id} />} />
        <Route path="databases" element={<DatabasesTab serviceId={service.id} />} />
        <Route path="domains" element={<DomainsTab key={service.id} service={service} onChange={reload} />} />
        <Route path="settings" element={<SettingsTab key={service.id} service={service} onChange={reload} />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  )
}

function ServiceSkeleton() {
  return (
    <>
      <header className="page-head">
        <div className="titles" style={{ gap: 10 }}>
          <Skeleton width={210} height={22} />
          <Skeleton width={340} height={12} />
        </div>
      </header>
      <ListSkeleton rows={4} />
    </>
  )
}

/* ------------------------------------------------------------------ */

function DeploysTab({ service }: { service: Detail }) {
  const inFlight = service.recent_deployments.some((d) => isInFlight(d.status))
  const { data, reload } = useApi(
    () => api.deploys(service.id),
    [service.id],
    inFlight ? 30_000 : undefined,
  )
  useLiveRefresh(reload, (event) => event.service_id === service.id)
  const deployments = data ?? service.recent_deployments
  const [watching, setWatching] = useState<Deployment | null>(null)
  const [activity, setActivity] = useState<DeployActivity | null>(null)

  // The drawer holds a deploy captured when it was opened; refresh it from the
  // list so the timeline inside keeps moving as events arrive.
  const watched = watching
    ? (deployments.find((item) => item.id === watching.id) ?? watching)
    : null

  if (deployments.length === 0) {
    return (
      <EmptyState icon={<IconInbox className="empty-art" size={46} />} title="No deploys yet">
        Push to <span className="mono">{service.branch}</span>, or use “Deploy application” above.
      </EmptyState>
    )
  }

  return (
    <>
      <div className="list">
        {deployments.map((deployment) => (
          <DeployRow
            key={deployment.id}
            deployment={deployment}
            isLive={deployment.id === service.live_deployment_id}
            onChange={reload}
            onWatch={() => setWatching(deployment)}
          />
        ))}
      </div>

      <Drawer
        open={watched !== null}
        onClose={() => setWatching(null)}
        title={
          watched ? (
            <span className="row" style={{ gap: 10 }}>
              <StatusBadge status={watched.status} />
              <span className="mono tiny">{shortSha(watched.commit_sha)}</span>
              <span className="truncate">{deployTitle(watched)}</span>
            </span>
          ) : (
            ''
          )
        }
        footer={
          watched && (
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="tiny faint">
                {watched.detected_runtime ?? 'runtime not detected yet'}
                {' · '}
                {duration(watched.duration_seconds)}
              </span>
              <Link className="btn sm" to={`/deploys/${watched.id}`}>
                Open full page
              </Link>
            </div>
          )
        }
      >
        {watched && (
          <>
            <DeployTimeline deployment={watched} activity={activity} />
            {watched.error_message && (
              <div className="alert error">{watched.error_message}</div>
            )}
            <Suspense fallback={<Skeleton height={320} radius={14} />}>
              <LogConsole deploymentId={watched.id} onFinished={reload} onActivity={setActivity} />
            </Suspense>
          </>
        )}
      </Drawer>
    </>
  )
}

function DeployRow({
  deployment,
  isLive,
  onChange,
  onWatch,
}: {
  deployment: Deployment
  isLive: boolean
  onChange: () => void
  onWatch: () => void
}) {
  const [busy, setBusy] = useState(false)
  const { attempt } = useToast()

  const rollback = async () => {
    setBusy(true)
    await attempt(() => api.rollback(deployment.id), {
      success: `Rolling back to ${shortSha(deployment.commit_sha)}`,
      failure: 'Could not roll back',
    })
    setBusy(false)
    onChange()
  }

  return (
    <div className="list-item">
      <StatusBadge status={deployment.status} />

      <Link className="grow" to={`/deploys/${deployment.id}`}>
        <div className="truncate">{deployTitle(deployment)}</div>
        <div className="tiny faint">
          <span className="mono">{shortSha(deployment.commit_sha)}</span>
          {deployment.commit_author && ` · ${deployment.commit_author}`}
          {` · ${deployment.trigger}`}
          {` · ${relativeTime(deployment.created_at)}`}
          {deployment.detected_runtime && ` · ${deployment.detected_runtime}`}
        </div>
      </Link>

      <span className="tiny faint tabular">{duration(deployment.duration_seconds)}</span>

      <Tooltip
        label={
          isInFlight(deployment.status)
            ? 'Follow this build without leaving the page'
            : 'Read this build’s log without leaving the page'
        }
      >
        <Button size="sm" variant="ghost" onClick={onWatch}>
          {isInFlight(deployment.status) ? 'Watch' : 'Log'}
        </Button>
      </Tooltip>

      {isLive ? (
        <span className="pill ok">
          <span className="dot" aria-hidden="true" />
          Current
        </span>
      ) : (
        deployment.status === 'live' && (
          <Button size="sm" loading={busy} onClick={rollback}>
            Roll back
          </Button>
        )
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */

function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let n = bytes / 1024
  let i = 0
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024
    i++
  }
  return `${n.toFixed(n < 10 ? 1 : 0)} ${units[i]}`
}

function AreaChart({ values, max }: { values: number[]; max: number }) {
  const W = 600
  const H = 90
  const pad = 4
  const n = values.length
  const top = Math.max(max, ...values, 1)
  const x = (i: number) => (n <= 1 ? W : (i / (n - 1)) * W)
  const y = (v: number) => H - pad - (v / top) * (H - pad * 2)
  const line = values
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
    .join(' ')
  const area = `${line} L${W},${H} L0,${H} Z`
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: 90, display: 'block' }}
      role="img"
      aria-label="usage over time"
    >
      <path d={area} fill="var(--brand)" opacity="0.12" />
      <path
        d={line}
        fill="none"
        stroke="var(--brand)"
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

function MetricCard({
  title,
  values,
  limit,
  format,
}: {
  title: string
  values: number[]
  limit: number
  format: (v: number) => string
}) {
  const latest = values[values.length - 1] ?? 0
  const pct = limit > 0 ? Math.round((latest / limit) * 100) : 0
  return (
    <div className="card card-pad-lg stack" style={{ gap: 10 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong>{title}</strong>
        <span className="tabular muted">
          {format(latest)} / {format(limit)} · {pct}%
        </span>
      </div>
      <AreaChart values={values} max={limit} />
    </div>
  )
}

function MetricsTab({ serviceId }: { serviceId: string }) {
  const { data, error, loading } = useApi(
    () => api.serviceMetrics(serviceId, 60),
    [serviceId],
    30_000,
  )

  if (error) return <div className="alert error">{error}</div>
  if (loading && !data) return <Skeleton height={220} />
  if (!data || data.points.length === 0) {
    return (
      <EmptyState icon={<IconInbox className="empty-art" size={46} />} title="No metrics yet">
        CPU and memory samples appear a minute or two after the service goes live,
        and refresh every 30 seconds.
      </EmptyState>
    )
  }

  return (
    <div className="stack">
      <MetricCard
        title="CPU"
        values={data.points.map((p) => p.cpu_millicores)}
        limit={data.cpu_limit_millicores}
        format={(v) => `${v}m`}
      />
      <MetricCard
        title="Memory"
        values={data.points.map((p) => p.memory_bytes)}
        limit={data.memory_limit_bytes}
        format={fmtBytes}
      />
    </div>
  )
}

function LogsTab({ serviceId }: { serviceId: string }) {
  // Still polled, and deliberately: runtime output is read straight from the
  // container on request, so there is no event to wait for. Build logs, which
  // do stream, use the WebSocket console instead.
  const { data, error, loading } = useApi(
    () => api.runtimeLogs(serviceId, 500),
    [serviceId],
    5_000,
  )

  if (error) return <div className="alert error">{error}</div>
  if (loading && !data) return <Skeleton height={220} />
  if (!data?.length) {
    return (
      <EmptyState icon={<IconInbox className="empty-art" size={46} />} title="No runtime output yet">
        Anything your app writes to stdout or stderr shows up here.
      </EmptyState>
    )
  }

  return (
    <div className="console-wrap">
      <div className="console-bar">
        <span className="tiny muted">Runtime output · last {data.length} lines</span>
        <CopyButton value={data.join('\n')} label="Copy all" />
      </div>
      <pre className="log-pre">{data.join('\n')}</pre>
    </div>
  )
}

/* ------------------------------------------------------------------ */

/** Parse pasted .env text into variables: KEY=VALUE lines, `export` prefixes
 *  and surrounding quotes tolerated, comments and blanks skipped. */
function parseDotEnv(text: string): { key: string; value: string; is_secret: boolean }[] {
  const vars: { key: string; value: string; is_secret: boolean }[] = []
  for (const raw of text.split('\n')) {
    const line = raw.trim().replace(/^export\s+/, '')
    if (!line || line.startsWith('#')) continue
    const eq = line.indexOf('=')
    if (eq < 1) continue
    const key = line.slice(0, eq).trim()
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(key)) continue
    let value = line.slice(eq + 1).trim()
    if (
      (value.startsWith('"') && value.endsWith('"') && value.length >= 2) ||
      (value.startsWith("'") && value.endsWith("'") && value.length >= 2)
    ) {
      value = value.slice(1, -1)
    }
    vars.push({ key, value, is_secret: true })
  }
  return vars
}

const KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/

function randomValue(length = 32): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  const bytes = new Uint8Array(length)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('')
}

/**
 * Environment variables, the way Render does it: every variable is a key and
 * value edited in place, values are masked until you ask, and nothing is
 * written until you press Save Changes — which applies the whole batch and
 * redeploys once. Rows removed or renamed are staged the same way.
 */
function EnvTab({ serviceId }: { serviceId: string }) {
  const { data, error, reload } = useApi(() => api.envVars(serviceId), [serviceId])
  const [rows, setRows] = useState<EnvRow[] | null>(null)
  const [showPaste, setShowPaste] = useState(false)
  const [bulk, setBulk] = useState('')
  const [busy, setBusy] = useState(false)
  const { attempt, notify } = useToast()

  // --- change detection -------------------------------------------------
  const current = rows ?? []
  // `rows`, not `current`: before the first load there is nothing staged, and
  // calling that "every variable deleted" is what left this section on a
  // loading placeholder forever.
  const { deletedKeys, changedRows, dirty } = changeSet(rows, data ?? [])

  // Refresh from the server only while nothing is being edited, so a live
  // event mid-edit never wipes the developer's unsaved work.
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty
  useEffect(() => {
    if (!data || dirtyRef.current) return
    setRows((existing) =>
      existing === null ? rowsFromServer(data) : mergeFromServer(existing, data),
    )
  }, [data])

  // --- validation ---------------------------------------------------------
  const keyCounts = new Map<string, number>()
  for (const row of current) {
    if (row.key) keyCounts.set(row.key, (keyCounts.get(row.key) ?? 0) + 1)
  }
  const problem = (row: EnvRow): string | null => {
    if (!row.key) return null
    if (!KEY_PATTERN.test(row.key)) return 'Keys are letters, digits and underscores'
    if ((keyCounts.get(row.key) ?? 0) > 1) return 'Duplicate key'
    // A renamed or new row must carry a value we actually have.
    const needsValue = row.originalKey === null || row.key !== row.originalKey || Boolean(row.buildTime) !== Boolean(row.originalBuildTime)
    if (needsValue && !row.known) return 'Reveal or re-enter the value to rename'
    return null
  }
  const valid = current.every((row) => problem(row) === null)

  // --- row editing --------------------------------------------------------
  const update = (id: string, patch: Partial<EnvRow>) =>
    setRows((list) => (list ?? []).map((row) => (row.id === id ? { ...row, ...patch } : row)))

  const addRow = () => {
    const id = `new-${Math.random().toString(36).slice(2)}`
    setRows((list) => [
      ...(list ?? []),
      { id, key: '', value: '', known: true, shown: true, originalKey: null, originalValue: null },
    ])
    requestAnimationFrame(() => document.getElementById(`env-key-${id}`)?.focus())
  }

  const removeRow = (id: string) =>
    setRows((list) => (list ?? []).filter((row) => row.id !== id))

  const toggleShown = async (row: EnvRow) => {
    if (row.known) {
      update(row.id, { shown: !row.shown })
      return
    }
    // An existing secret: fetch it (audited) and make it editable in place.
    try {
      const { value } = await api.revealEnvVar(serviceId, row.originalKey ?? row.key)
      update(row.id, { value, known: true, shown: true, originalValue: value })
    } catch {
      notify('Could not reveal the value', 'error')
    }
  }

  const parsed = parseDotEnv(bulk)
  const addFromDotEnv = () => {
    setRows((list) => {
      const next = [...(list ?? [])]
      for (const item of parsed) {
        const existing = next.find((row) => row.key === item.key)
        if (existing) {
          Object.assign(existing, { value: item.value, known: true })
        } else {
          next.push({
            id: `new-${Math.random().toString(36).slice(2)}`,
            key: item.key,
            value: item.value,
            known: true,
            shown: false,
            originalKey: null,
            originalValue: null,
          })
        }
      }
      return next
    })
    setBulk('')
    setShowPaste(false)
  }

  const discard = () => {
    if (data) setRows(rowsFromServer(data))
  }

  // --- save ----------------------------------------------------------------
  const save = async () => {
    setBusy(true)
    // The whole environment as it should end up, in one request. Sending the
    // desired state rather than a list of operations is what makes the
    // promise above true: one transaction, one redeploy, and no window where
    // a rename has deleted the old key and not yet written the new one.
    //
    // A secret nobody revealed is sent without a value, which the API reads
    // as "keep what is stored" — this page cannot send a value it was never
    // given, and must not claim the variable is gone either.
    const desired = current
      .filter((row) => row.key !== '')
      .map((row) => ({
        key: row.key,
        value: row.known ? row.value : null,
        is_secret: !row.buildTime,
        build_time: Boolean(row.buildTime),
      }))
    // A rename is one change, and `changedRows` already counts it as one.
    const count = changedRows.length + deletedKeys.length

    const ok = await attempt(
      async () => {
        await api.setEnvVars(serviceId, desired, true)
      },
      {
        success: `${count} change${count === 1 ? '' : 's'} saved — redeploying`,
        failure: 'Could not save the environment',
      },
    )
    setBusy(false)
    if (ok) {
      // What is on screen *is* what was just saved, so it becomes the new
      // baseline in place: same rows, same order, same revealed values, and
      // nothing dirty. Discarding them and refetching is what made a save
      // look like it had emptied the section.
      setRows((list) => rebaseAfterSave(list ?? []))
      reload()
    }
  }

  return (
    <div className="stack">
      <div className="alert info">
        Values are encrypted at rest and masked until you reveal them. Edits are
        staged here — press <b>Save Changes</b> to apply them all and redeploy once.
      </div>

      {rows === null && error ? (
        // A request that failed used to render as a loading placeholder that
        // never resolved, which says "working on it" about something that has
        // already stopped. Say what happened and offer the one useful action.
        <div className="alert error">
          <span>Could not load the environment: {error}</span>{' '}
          <button type="button" className="btn" onClick={reload}>
            Try again
          </button>
        </div>
      ) : rows === null ? (
        <ListSkeleton rows={2} />
      ) : (
        <>
          <div className="list">
            {current.length > 0 && (
              <div className="env-head">
                <span>Key</span>
                <span>Value</span>
                <span />
              </div>
            )}
            {current.map((row) => {
              const issue = problem(row)
              return (
                <div className="env-row" key={row.id}>
                  <input
                    id={`env-key-${row.id}`}
                    className={`mono${issue && row.key ? ' invalid' : ''}`}
                    placeholder="KEY"
                    aria-label="Variable name"
                    title={issue ?? undefined}
                    value={row.key}
                    onChange={(event) => update(row.id, { key: event.target.value })}
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                  <div className="env-value">
                    <input
                      className="mono"
                      type={row.shown ? 'text' : 'password'}
                      placeholder={row.known ? 'value' : '••••••••'}
                      aria-label="Variable value"
                      value={row.value}
                      onChange={(event) =>
                        update(row.id, { value: event.target.value, known: true })
                      }
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                    <button
                      type="button"
                      className="btn ghost icon"
                      aria-label={row.shown ? 'Hide value' : 'Show value'}
                      title={row.shown ? 'Hide value' : 'Show value'}
                      onClick={() => toggleShown(row)}
                    >
                      {row.shown ? (
                        <IconEyeOff className="" size={15} />
                      ) : (
                        <IconEye className="" size={15} />
                      )}
                    </button>
                  </div>
                  <div className="env-row-actions">
                    <label className="tiny" title="Include this non-secret value in the build image. Never select this for passwords or API keys.">
                      <input type="checkbox" checked={Boolean(row.buildTime)} onChange={(event) => update(row.id, { buildTime: event.target.checked })} /> Public build value
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Fill with a random 32-character value"
                      onClick={() =>
                        update(row.id, { value: randomValue(), known: true, shown: true })
                      }
                    >
                      Generate
                    </Button>
                    <button
                      type="button"
                      className="btn ghost icon"
                      aria-label={`Remove ${row.key || 'variable'}`}
                      title="Remove"
                      onClick={() => removeRow(row.id)}
                    >
                      <IconTrash className="" size={16} />
                    </button>
                  </div>
                </div>
              )
            })}
            {current.length === 0 && (
              <div className="list-item muted tiny">
                No environment variables yet. Add one below, or paste a .env file.
              </div>
            )}
          </div>

          <div className="row">
            <Button variant="secondary" onClick={addRow}>
              <IconPlus className="" size={16} />
              Add Environment Variable
            </Button>
            <Button variant="ghost" onClick={() => setShowPaste((open) => !open)}>
              Add from .env
            </Button>
          </div>

          {showPaste && (
            <div className="card card-pad-lg stack">
              <Field
                label="Paste .env contents"
                htmlFor="env-bulk"
                hint="One KEY=VALUE per line — comments and blank lines are ignored. Existing keys are updated, new ones added. Nothing is saved until you press Save Changes."
              >
                <textarea
                  id="env-bulk"
                  className="mono"
                  rows={8}
                  placeholder={'DATABASE_URL=postgres://…\nSECRET_KEY=…\nSTRIPE_KEY=…'}
                  value={bulk}
                  onChange={(event) => setBulk(event.target.value)}
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </Field>
              <div className="row">
                <Button variant="primary" disabled={parsed.length === 0} onClick={addFromDotEnv}>
                  {parsed.length === 0
                    ? 'Add variables'
                    : `Add ${parsed.length} variable${parsed.length === 1 ? '' : 's'}`}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setBulk('')
                    setShowPaste(false)
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {dirty && (
            <div className="env-footer">
              <span className="tiny muted">
                {valid
                  ? 'You have unsaved changes. Saving redeploys the service.'
                  : 'Fix the highlighted rows before saving.'}
              </span>
              <div className="row">
                <Button variant="ghost" onClick={discard} disabled={busy}>
                  Discard
                </Button>
                <Button variant="primary" loading={busy} disabled={!valid} onClick={save}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */

function DatabasesTab({ serviceId }: { serviceId: string }) {
  const { data: links, error: linksError, reload } = useApi(
    () => api.serviceDatabases(serviceId),
    [serviceId],
  )
  const { data: available, error: availableError, reload: retryAvailable } = useApi(() => api.databases(), [])

  const [selected, setSelected] = useState('')
  const [variable, setVariable] = useState('')
  const [busy, setBusy] = useState(false)
  const { attempt } = useToast()

  const linkedIds = new Set((links ?? []).map((row) => row.database_id))
  const attachable = (available ?? []).filter(
    (database) => !linkedIds.has(database.id) && database.status !== 'deleted',
  )

  const act = async (fn: () => Promise<unknown>, success: string) => {
    setBusy(true)
    await attempt(fn, { success })
    setBusy(false)
    setSelected('')
    setVariable('')
    reload()
  }

  return (
    <div className="stack">
      <div className="alert info">
        Linking injects the connection string as an environment variable and
        redeploys. A linked value always wins over one set by hand, so there is
        no stale URL to debug.
      </div>

      {linksError ? <RequestError message={linksError} retry={reload} /> : !links ? (
        <ListSkeleton rows={1} />
      ) : (
        <div className="list">
          {links.map((row) => (
            <div className="list-item" key={row.id}>
              <div className="grow">
                <Link to={`/databases/${row.database_id}`} className="truncate">
                  {row.database_name}
                </Link>
                <div className="tiny faint">
                  {engineName(row.engine)} · injected as{' '}
                  <span className="mono">{row.env_var_name}</span>
                </div>
              </div>
              <DatabaseBadge status={row.status} />
              <Button
                variant="danger"
                size="sm"
                disabled={busy}
                onClick={() =>
                  act(
                    () => api.unlinkDatabase(serviceId, row.id),
                    `${row.env_var_name} unlinked — redeploying`,
                  )
                }
              >
                Unlink
              </Button>
            </div>
          ))}
          {links.length === 0 && (
            <div className="list-item muted tiny">No databases linked.</div>
          )}
        </div>
      )}

      <div className="card card-pad-lg stack">
        {availableError ? <RequestError message={availableError} retry={retryAvailable} /> : !available ? <p role="status">Loading available databases…</p> : attachable.length === 0 ? (
          <p className="muted">
            No unlinked databases available.{' '}
            <Link to="/databases" style={{ color: 'var(--brand-text)' }}>
              Create one
            </Link>
            .
          </p>
        ) : (
          <>
            <div className="field-row">
              <Field label="Database" htmlFor="link-db">
                <select
                  id="link-db"
                  value={selected}
                  onChange={(event) => setSelected(event.target.value)}
                >
                  <option value="">Choose a database…</option>
                  {attachable.map((database) => (
                    <option key={database.id} value={database.id}>
                      {database.name} ({database.engine})
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Variable name"
                htmlFor="link-var"
                hint="Leave blank for the default — DATABASE_URL or REDIS_URL."
              >
                <input
                  id="link-var"
                  className="mono"
                  placeholder="DATABASE_URL"
                  value={variable}
                  onChange={(event) => setVariable(event.target.value)}
                />
              </Field>
            </div>

            <div>
              <Button
                variant="primary"
                disabled={!selected}
                loading={busy}
                onClick={() =>
                  act(
                    () => api.linkDatabase(serviceId, selected, variable || undefined),
                    'Database linked — redeploying',
                  )
                }
              >
                Link and redeploy
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */

/** Whether a push to this repository will actually reach the platform.
 *
 * Reads the record the API keeps per repository. Anything other than active
 * is shown with the reason and a Repair button, because each status is a
 * different thing for the developer to do — reconnect GitHub, get admin on
 * the repository, or nothing, in which case the platform retries on its own.
 */
function WebhookState({ service, onChange }: { service: Detail; onChange: () => void }) {
  const { attempt } = useToast()
  const [busy, setBusy] = useState(false)
  const hook = service.webhook

  const repair = async () => {
    setBusy(true)
    await attempt(() => api.repairWebhook(service.id), {
      success: 'Asked GitHub for the webhook again',
      failure: 'Could not reach the platform to ask',
    })
    setBusy(false)
    onChange()
  }

  if (!hook) {
    return (
      <div className="row" style={{ alignItems: 'center', gap: 12 }}>
        <p className="hint grow">
          GitHub has not been asked for the push webhook yet — the platform does
          that within ten minutes.
        </p>
        <Button variant="ghost" loading={busy} onClick={repair}>
          Ask now
        </Button>
      </div>
    )
  }

  if (hook.status === 'active') {
    return (
      <p className="hint">
        ✓ GitHub will notify this platform of pushes to{' '}
        <span className="mono">{service.repo_full_name}</span>.
      </p>
    )
  }

  // Every other status: the reason, and what to do. The API's wording is
  // written for the developer; it is shown as is.
  const canRepairHere = hook.status !== 'unconfigured'
  return (
    <div className="alert warn" role="status">
      <div className="grow">
        <strong>Pushes will not deploy yet.</strong> {hook.error}
        {hook.status === 'token_invalid' || hook.status === 'no_token' ? (
          <>
            {' '}
            <Link to="/account">Open Account</Link>
          </>
        ) : null}
      </div>
      {canRepairHere && (
        <Button variant="ghost" loading={busy} onClick={repair}>
          Repair
        </Button>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */

function SettingsTab({ service, onChange }: { service: Detail; onChange: () => void }) {
  const navigate = useNavigate()
  const { attempt } = useToast()
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)

  const [form, setForm] = useState({
    name: service.name,
    branch: service.branch,
    port: service.port,
    health_check_path: service.health_check_path,
    start_command: service.start_command ?? '',
    pre_deploy_command: service.pre_deploy_command ?? '',
    build_command: service.build_command ?? '',
    replicas: service.replicas,
    auto_deploy: service.auto_deploy,
    check_command: service.check_command ?? '',
    edge_enabled: service.edge_enabled,
    edge_cache_ttl: service.edge_cache_ttl,
    guard_enabled: service.guard_enabled,
    guard_rate_average: service.guard_rate_average,
    guard_rate_burst: service.guard_rate_burst,
    proxy_service_id: service.proxy_service_id ?? '',
    proxy_path_prefix: service.proxy_path_prefix || '/api',
    proxy_strip_prefix: service.proxy_strip_prefix,
    type: service.type,
    publish_directory: service.publish_directory ?? '',
  })

  const canEdge = service.type === 'web' || service.type === 'static'
  // Talari Link: the other components of this application that could answer
  // an API path on this service's address. Only those with a public URL and
  // not a static site — and never this service itself.
  const { data: application } = useApi(
    () => (service.project_id ? api.project(service.project_id) : Promise.resolve(null)),
    [service.project_id],
  )
  const linkTargets = (application?.services ?? []).filter(
    (s) => s.id !== service.id && (s.type === 'web' || s.type === 'function'),
  )
  const canLink = canEdge || service.type === 'function'
  // Guard (rate limiting) fits any service with a public URL — functions too.
  const canGuard =
    service.type === 'web' || service.type === 'static' || service.type === 'function'

  const { data: plans } = useApi(() => api.plans(), [])

  const save = async () => {
    setBusy(true)
    await attempt(
      () =>
        api.updateService(service.id, {
          ...form,
          start_command: form.start_command || null,
          pre_deploy_command: form.pre_deploy_command || null,
          build_command: form.build_command || null,
          check_command: form.check_command || null,
        }),
      { success: 'Settings saved', failure: 'Could not save' },
    )
    setBusy(false)
    onChange()
  }

  const destroy = async () => {
    const ok = await attempt(() => api.deleteService(service.id), {
      success: `${service.name} deleted`,
      failure: 'Could not delete the service',
    })
    setConfirming(false)
    if (ok) navigate('/services')
  }

  return (
    <div className="stack">
      <div className="card card-pad-lg stack">
        <div className="field-row">
          <Field label="Name" htmlFor="s-name">
            <input
              id="s-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <Field label="Branch" htmlFor="s-branch">
            <input
              id="s-branch"
              className="mono"
              value={form.branch}
              onChange={(e) => setForm({ ...form, branch: e.target.value })}
            />
          </Field>
        </div>

        <div className="field-row">
          <Field label="Port" htmlFor="s-port" hint="TalariCloud sets PORT in your environment.">
            <input
              id="s-port"
              type="number"
              className="tabular"
              value={form.port}
              onChange={(e) => setForm({ ...form, port: Number(e.target.value) })}
            />
          </Field>
          <Field
            label="Health check path"
            htmlFor="s-health"
            hint="Leave as / to check only that the app is listening on its port. Set a path, e.g. /healthz, to require an answer below 400."
          >
            <input
              id="s-health"
              className="mono"
              value={form.health_check_path}
              onChange={(e) => setForm({ ...form, health_check_path: e.target.value })}
            />
          </Field>
        </div>

        {/* The plan belongs to the application, not to this component: the
            frontend, the API and the worker are charged once and share one
            pool of compute. It is changed on the application page, where
            the whole pool and every component's share are in view. */}
        <div className="alert info">
          {service.type === 'static' ? (
            <>
              <strong>Included free.</strong> A static site is served from the
              edge and has no plan of its own — it comes with the application
              at no charge.
            </>
          ) : (
            <>
              <strong>{plans?.find((plan) => plan.tier === service.plan)?.label ?? service.plan} plan</strong>,
              shared with the rest of this application. One plan covers the
              frontend, backend and workers together; change it on the{' '}
              <Link to={`/projects/${service.project_id}`}>application page</Link>
              , where the compute pool and each component's share are shown.
              The database behind this service is moved on its own page.
            </>
          )}
        </div>

        <Field
          label="Build command"
          htmlFor="s-build"
          hint="Leave blank to let Nixpacks decide. A Go module with several commands under cmd/ needs one, e.g. go build -o out ./cmd/server."
        >
          <input
            id="s-build"
            className="mono"
            placeholder="detected automatically"
            value={form.build_command}
            onChange={(e) => setForm({ ...form, build_command: e.target.value })}
          />
        </Field>

        <Field
          label="Start command"
          htmlFor="s-start"
          hint="Leave blank to let Nixpacks decide."
        >
          <input
            id="s-start"
            className="mono"
            placeholder="detected automatically"
            value={form.start_command}
            onChange={(e) => setForm({ ...form, start_command: e.target.value })}
          />
        </Field>

        <Field
          label="Pre-deploy command"
          htmlFor="s-pre-deploy"
          hint="Optional migrations. Runs after database readiness and before app rollout, without the service disk. Use backward-compatible migrations."
        >
          <input id="s-pre-deploy" className="mono" value={form.pre_deploy_command}
            onChange={(e) => setForm({ ...form, pre_deploy_command: e.target.value })} />
        </Field>

        <Field
          label="Test command (Talari CI)"
          htmlFor="s-check"
          hint="Runs in the built image on every deploy. A non-zero exit blocks the release — the previous version keeps serving."
        >
          <input
            id="s-check"
            className="mono"
            placeholder="e.g. pytest -q   ·   blank to skip"
            value={form.check_command}
            onChange={(e) => setForm({ ...form, check_command: e.target.value })}
          />
        </Field>

        <label className="check">
          <input
            type="checkbox"
            checked={form.auto_deploy}
            onChange={(e) => setForm({ ...form, auto_deploy: e.target.checked })}
          />
          <span>
            Deploy automatically on every push to{' '}
            <span className="mono">{form.branch}</span>
            {/* Said here because the behaviour is not what the checkbox alone
                implies, and finding that out from a deploy log is worse. */}
            <small className="hint" style={{ display: 'block', marginTop: 4 }}>
              A push releases the whole application — every component, from the
              same commit. Turning this off means your pushes stop starting a
              release; it does not leave this component out of one.
            </small>
          </span>
        </label>

        {/* The checkbox above is a promise; this is whether GitHub has been
            told to keep it. For a long time it had not, and the only sign
            was a push that never became a deploy. */}
        {form.auto_deploy && (
          <WebhookState service={service} onChange={onChange} />
        )}

        {canEdge && (
          <div className="stack" style={{ gap: 10 }}>
            <label className="check">
              <input
                type="checkbox"
                checked={form.edge_enabled}
                onChange={(e) => setForm({ ...form, edge_enabled: e.target.checked })}
              />
              <span>
                <strong>Talari Edge</strong> — compress responses at the Addis
                ingress{' '}
                {service.type === 'static'
                  ? 'and cache static assets'
                  : '(Brotli / gzip)'}
              </span>
            </label>
            {form.edge_enabled && service.type === 'static' && (
              <Field
                label="Cache lifetime (seconds)"
                htmlFor="s-edge-ttl"
                hint="How long browsers and the edge may reuse a cached response."
              >
                <input
                  id="s-edge-ttl"
                  type="number"
                  min={0}
                  max={86400}
                  className="tabular"
                  value={form.edge_cache_ttl}
                  onChange={(e) =>
                    setForm({ ...form, edge_cache_ttl: Number(e.target.value) })
                  }
                />
              </Field>
            )}
          </div>
        )}

        {/* The one type change that is allowed, and the one people need: a
            frontend created as a web service can never start, and before this
            the only way out was to delete it. */}
        {(service.type === 'web' || service.type === 'static') && (
          <Field
            label="Service type"
            htmlFor="s-type"
            hint={
              service.type === 'web'
                ? 'A web service runs a server that listens on a port. If this is a Vite, Create React App or similar frontend, it builds a folder of files and runs no server — make it a static site. It keeps this address, its environment and its history, is served from the edge, and costs nothing.'
                : 'A static site serves built files from the edge and is included free with the application. Make it a web service only if it needs to run a server.'
            }
          >
            <select
              id="s-type"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value as typeof form.type })}
            >
              <option value="web">Web service — runs a server</option>
              <option value="static">Static site — serves built files, free</option>
            </select>
          </Field>
        )}
        {form.type === 'static' && (
          <Field
            label="Publish directory"
            htmlFor="s-publish"
            hint="The folder the build writes the site into — dist for Vite, build for Create React App. Leave blank and the platform looks for the usual ones."
          >
            <input
              id="s-publish"
              className="mono"
              placeholder="dist"
              value={form.publish_directory}
              onChange={(e) => setForm({ ...form, publish_directory: e.target.value })}
            />
          </Field>
        )}
        {form.type !== service.type && (
          <div className="alert info">
            Saving rebuilds this service as a{' '}
            {form.type === 'static' ? 'static site' : 'web service'}. Its address,
            environment variables and history are kept.
          </div>
        )}

        {canLink && linkTargets.length > 0 && (
          <div className="stack" style={{ gap: 10 }}>
            <Field
              label="Talari Link — API on the same address"
              htmlFor="s-link"
              hint="Route a path on this service's address to another component of the application. The browser sees one origin: no CORS to configure, cookies just work, one address to share."
            >
              <select
                id="s-link"
                value={form.proxy_service_id}
                onChange={(e) => setForm({ ...form, proxy_service_id: e.target.value })}
              >
                <option value="">Off — this service answers every path itself</option>
                {linkTargets.map((target) => (
                  <option key={target.id} value={target.id}>
                    {target.name} answers {form.proxy_path_prefix || '/api'}
                  </option>
                ))}
              </select>
            </Field>
            {form.proxy_service_id && (
              <div className="field-row">
                <Field
                  label="Routed path"
                  htmlFor="s-link-prefix"
                  hint="Requests under this prefix go to the linked component. Point the frontend's API base URL at this path instead of the backend's own address."
                >
                  <input
                    id="s-link-prefix"
                    className="mono"
                    value={form.proxy_path_prefix}
                    onChange={(e) => setForm({ ...form, proxy_path_prefix: e.target.value })}
                  />
                </Field>
                <label className="check" style={{ alignSelf: 'end' }}>
                  <input
                    type="checkbox"
                    checked={form.proxy_strip_prefix}
                    onChange={(e) => setForm({ ...form, proxy_strip_prefix: e.target.checked })}
                  />
                  <span>
                    <strong>Remove the prefix</strong> before forwarding — for a backend
                    that serves at <span className="mono">/</span> rather than under{' '}
                    <span className="mono">{form.proxy_path_prefix || '/api'}</span>
                  </span>
                </label>
              </div>
            )}
          </div>
        )}

        {canGuard && (
          <div className="stack" style={{ gap: 10 }}>
            <label className="check">
              <input
                type="checkbox"
                checked={form.guard_enabled}
                onChange={(e) => setForm({ ...form, guard_enabled: e.target.checked })}
              />
              <span>
                <strong>Talari Guard</strong> — rate-limit requests per client
                IP, absorbing abuse before it reaches your app
              </span>
            </label>
            {form.guard_enabled && (
              <div className="field-row">
                <Field
                  label="Requests / second (per IP)"
                  htmlFor="s-guard-avg"
                  hint="Sustained rate one client may keep up."
                >
                  <input
                    id="s-guard-avg"
                    type="number"
                    min={1}
                    max={10000}
                    className="tabular"
                    value={form.guard_rate_average}
                    onChange={(e) =>
                      setForm({ ...form, guard_rate_average: Number(e.target.value) })
                    }
                  />
                </Field>
                <Field
                  label="Burst"
                  htmlFor="s-guard-burst"
                  hint="Short spike allowed above the sustained rate."
                >
                  <input
                    id="s-guard-burst"
                    type="number"
                    min={1}
                    max={20000}
                    className="tabular"
                    value={form.guard_rate_burst}
                    onChange={(e) =>
                      setForm({ ...form, guard_rate_burst: Number(e.target.value) })
                    }
                  />
                </Field>
              </div>
            )}
          </div>
        )}

        <div>
          <Button variant="primary" loading={busy} onClick={save}>
            Save changes
          </Button>
        </div>
      </div>

      <h2 className="section-title">Danger zone</h2>
      <div className="card row" style={{ justifyContent: 'space-between' }}>
        <div>
          <strong>Delete this service</strong>
          <p className="tiny muted">
            Stops every instance and releases{' '}
            <span className="mono">{service.slug}</span>. Attached disks are kept.
          </p>
        </div>
        <Button variant="danger" onClick={() => setConfirming(true)}>
          Delete service
        </Button>
      </div>

      <ConfirmDialog
        open={confirming}
        destructive
        title={`Delete ${service.name}?`}
        requireText={service.name}
        confirmLabel="Delete service"
        body="Every instance stops and the hostname is released. Deploy history is kept for audit."
        onConfirm={destroy}
        onCancel={() => setConfirming(false)}
      />
    </div>
  )
}


